// src/components/admin/payroll/components/OTIncentivePanel.js
// OT and Incentives management panel for payroll processing

import React, { useState, useEffect, useCallback } from 'react';
import {
    Clock, Gift, Settings, Plus, Trash2, Save, X, ChevronDown, ChevronUp,
    AlertCircle, CheckCircle, DollarSign, Users
} from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import payrollService from '../../../../services/payroll.service';
import { toISTDateString } from '../../../../utils/dateHelpers';

const OTIncentivePanel = ({
    period,
    employees = [],
    otConfig: initialOTConfig,
    incentives: initialIncentives = [],
    onOTConfigChange,
    onIncentivesChange,
    onApply,
    loading = false,
    otDecisions = {} // OT decisions from OTValidationWizard (pay_ot, dont_consider, grant_compoff, ignore)
}) => {
    const { theme } = useTheme();

    // OT Configuration State
    const [otConfig, setOTConfig] = useState({
        enabled: false,
        rates: {
            regular: 1.5,
            holiday: 2.0,
            weekly_off: 2.0
        },
        threshold: 8,
        calculation_base: 'basic',
        fixed_hourly_rate: 100,
        ...initialOTConfig
    });

    // Incentives State
    const [incentives, setIncentives] = useState(initialIncentives);
    const [newIncentive, setNewIncentive] = useState({
        employee_id: '',
        type: 'performance_bonus',
        amount: '',
        reason: ''
    });

    // UI State
    const [showOTSettings, setShowOTSettings] = useState(false);
    const [showAddIncentive, setShowAddIncentive] = useState(false);
    const [employeesWithOT, setEmployeesWithOT] = useState([]);
    const [otCalculations, setOTCalculations] = useState({});
    const [savingConfig, setSavingConfig] = useState(false);

    // Incentive Types
    const incentiveTypes = [
        { value: 'performance_bonus', label: 'Performance Bonus' },
        { value: 'joining_bonus', label: 'Joining Bonus' },
        { value: 'retention_bonus', label: 'Retention Bonus' },
        { value: 'variable_pay', label: 'Variable Pay' },
        { value: 'ad_hoc', label: 'Ad-hoc Bonus' },
        { value: 'referral_bonus', label: 'Referral Bonus' }
    ];

    // Calculate OT for employees
    // Now filters by OT decisions (only pay_ot hours are included in calculation)
    const calculateOTForEmployees = useCallback(() => {
        if (!otConfig.enabled || !employees.length) {
            setEmployeesWithOT([]);
            setOTCalculations({});
            return;
        }

        const empsWithOT = [];
        const calculations = {};

        employees.forEach(emp => {
            const empOtHours = emp.otHours || emp.overtime || emp.ot_hours || {};
            const empDecisions = otDecisions?.[emp.id]?.decisions || {};
            const hasDecisions = Object.keys(empDecisions).length > 0;

            let regularOT = 0;
            let holidayOT = 0;

            // Filter OT hours by decisions if details are available
            if (empOtHours.details && Array.isArray(empOtHours.details) && hasDecisions) {
                empOtHours.details.forEach(record => {
                    // FIX: Use toISTDateString for timezone-safe date conversion
                    const dateStr = toISTDateString(record.date);

                    const decision = empDecisions[dateStr];
                    const action = decision?.action || 'dont_consider'; // Default: exclude

                    // Only include hours where action is 'pay_ot'
                    if (action === 'pay_ot') {
                        const hours = parseFloat(record.workingHours || record.hours || 0);
                        const dayType = record.overtimeReason || record.dayType;
                        if (dayType === 'regular') {
                            regularOT += hours;
                        } else {
                            holidayOT += hours;
                        }
                    }
                });
            } else if (!hasDecisions) {
                // No decisions saved: Default to EXCLUDING all OT (as per user requirement)
                regularOT = 0;
                holidayOT = 0;
            } else {
                // Fallback: Use aggregated hours (backward compatibility)
                regularOT = empOtHours.regular || empOtHours.regularHours || 0;
                holidayOT = empOtHours.holiday || empOtHours.holidayHours || 0;
            }

            const totalOT = regularOT + holidayOT;

            // Only add to list if there are payable OT hours
            if (totalOT > 0) {
                // Calculate hourly rate based on config
                const basicSalary = emp.basic_salary || emp.basicSalary || 0;
                const grossSalary = emp.gross_salary || emp.grossSalary || 0;

                let hourlyRate;
                switch (otConfig.calculation_base) {
                    case 'gross':
                        hourlyRate = (grossSalary / 30) / 8;
                        break;
                    case 'fixed_hourly':
                        hourlyRate = otConfig.fixed_hourly_rate || 100;
                        break;
                    default:
                        hourlyRate = (basicSalary / 30) / 8;
                }

                const regularOTPay = regularOT * hourlyRate * otConfig.rates.regular;
                const holidayOTPay = holidayOT * hourlyRate * otConfig.rates.holiday;
                const totalOTPay = Math.round(regularOTPay + holidayOTPay);

                calculations[emp.id] = {
                    regularOT,
                    holidayOT,
                    totalOT,
                    hourlyRate: Math.round(hourlyRate),
                    regularOTPay: Math.round(regularOTPay),
                    holidayOTPay: Math.round(holidayOTPay),
                    totalOTPay
                };

                empsWithOT.push({
                    ...emp,
                    otCalculation: calculations[emp.id]
                });
            }
        });

        setEmployeesWithOT(empsWithOT);
        setOTCalculations(calculations);
    }, [employees, otConfig, otDecisions]);

    useEffect(() => {
        calculateOTForEmployees();
    }, [calculateOTForEmployees]);

    // Handle OT config change
    const handleOTConfigChange = (field, value) => {
        const newConfig = { ...otConfig };

        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            newConfig[parent] = { ...newConfig[parent], [child]: value };
        } else {
            newConfig[field] = value;
        }

        setOTConfig(newConfig);
        onOTConfigChange?.(newConfig);
    };

    // Save OT config to backend
    const saveOTConfig = async () => {
        setSavingConfig(true);
        try {
            await payrollService.updateOTConfig(otConfig);
            // Show success feedback
        } catch (err) {
            console.error('Error saving OT config:', err);
        } finally {
            setSavingConfig(false);
        }
    };

    // Add new incentive
    const handleAddIncentive = () => {
        if (!newIncentive.employee_id || !newIncentive.amount) return;

        const employee = employees.find(e => e.id === newIncentive.employee_id);
        const incentive = {
            id: `inc-${Date.now()}`,
            ...newIncentive,
            amount: parseFloat(newIncentive.amount),
            employee_name: employee ? `${employee.first_name} ${employee.last_name}` : '',
            created_at: new Date().toISOString()
        };

        const updatedIncentives = [...incentives, incentive];
        setIncentives(updatedIncentives);
        onIncentivesChange?.(updatedIncentives);

        // Reset form
        setNewIncentive({
            employee_id: '',
            type: 'performance_bonus',
            amount: '',
            reason: ''
        });
        setShowAddIncentive(false);
    };

    // Remove incentive
    const handleRemoveIncentive = (incentiveId) => {
        const updatedIncentives = incentives.filter(inc => inc.id !== incentiveId);
        setIncentives(updatedIncentives);
        onIncentivesChange?.(updatedIncentives);
    };

    // Calculate totals
    const totalOTPay = Object.values(otCalculations).reduce((sum, calc) => sum + calc.totalOTPay, 0);
    const totalIncentives = incentives.reduce((sum, inc) => sum + (parseFloat(inc.amount) || 0), 0);

    return (
        <div className={`rounded-lg border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <div className={`p-4 border-b ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                            theme === 'dark' ? 'bg-purple-900/30' : 'bg-purple-100'
                        }`}>
                            <DollarSign className={`w-5 h-5 ${
                                theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                            }`} />
                        </div>
                        <div>
                            <h3 className={`font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Overtime & Incentives
                            </h3>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                                Review and configure OT pay and bonuses
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowOTSettings(!showOTSettings)}
                            className={`p-2 rounded-lg ${
                                theme === 'dark'
                                    ? 'hover:bg-gray-700 text-gray-400'
                                    : 'hover:bg-gray-100 text-gray-500'
                            }`}
                            title="OT Settings"
                        >
                            <Settings className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* OT Settings Panel */}
            {showOTSettings && (
                <div className={`p-4 border-b ${
                    theme === 'dark' ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
                }`}>
                    <div className="flex items-center justify-between mb-4">
                        <h4 className={`font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            OT Configuration
                        </h4>
                        <button
                            onClick={saveOTConfig}
                            disabled={savingConfig}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            {savingConfig ? 'Saving...' : 'Save Config'}
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Enable OT */}
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                id="ot-enabled"
                                checked={otConfig.enabled}
                                onChange={(e) => handleOTConfigChange('enabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded"
                            />
                            <label htmlFor="ot-enabled" className={`text-sm ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                                Enable Overtime Calculation
                            </label>
                        </div>

                        {/* Regular OT Rate */}
                        <div>
                            <label className={`block text-sm mb-1 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Regular OT Rate
                            </label>
                            <select
                                value={otConfig.rates.regular}
                                onChange={(e) => handleOTConfigChange('rates.regular', parseFloat(e.target.value))}
                                disabled={!otConfig.enabled}
                                className={`w-full px-3 py-2 rounded-lg text-sm ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } border disabled:opacity-50`}
                            >
                                <option value={1}>1x (Regular Pay)</option>
                                <option value={1.5}>1.5x</option>
                                <option value={2}>2x</option>
                                <option value={2.5}>2.5x</option>
                            </select>
                        </div>

                        {/* Holiday OT Rate */}
                        <div>
                            <label className={`block text-sm mb-1 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Holiday OT Rate
                            </label>
                            <select
                                value={otConfig.rates.holiday}
                                onChange={(e) => handleOTConfigChange('rates.holiday', parseFloat(e.target.value))}
                                disabled={!otConfig.enabled}
                                className={`w-full px-3 py-2 rounded-lg text-sm ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } border disabled:opacity-50`}
                            >
                                <option value={1.5}>1.5x</option>
                                <option value={2}>2x</option>
                                <option value={2.5}>2.5x</option>
                                <option value={3}>3x</option>
                            </select>
                        </div>

                        {/* Calculation Base */}
                        <div>
                            <label className={`block text-sm mb-1 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Calculation Base
                            </label>
                            <select
                                value={otConfig.calculation_base}
                                onChange={(e) => handleOTConfigChange('calculation_base', e.target.value)}
                                disabled={!otConfig.enabled}
                                className={`w-full px-3 py-2 rounded-lg text-sm ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } border disabled:opacity-50`}
                            >
                                <option value="basic">Basic Salary</option>
                                <option value="gross">Gross Salary</option>
                                <option value="fixed_hourly">Fixed Hourly Rate</option>
                            </select>
                        </div>

                        {/* Fixed Hourly Rate (conditional) */}
                        {otConfig.calculation_base === 'fixed_hourly' && (
                            <div>
                                <label className={`block text-sm mb-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Fixed Hourly Rate (₹)
                                </label>
                                <input
                                    type="number"
                                    value={otConfig.fixed_hourly_rate}
                                    onChange={(e) => handleOTConfigChange('fixed_hourly_rate', parseFloat(e.target.value))}
                                    disabled={!otConfig.enabled}
                                    className={`w-full px-3 py-2 rounded-lg text-sm ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    } border disabled:opacity-50`}
                                />
                            </div>
                        )}

                        {/* Threshold Hours */}
                        <div>
                            <label className={`block text-sm mb-1 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Daily Threshold (hrs)
                            </label>
                            <input
                                type="number"
                                value={otConfig.threshold}
                                onChange={(e) => handleOTConfigChange('threshold', parseInt(e.target.value))}
                                disabled={!otConfig.enabled}
                                min={1}
                                max={12}
                                className={`w-full px-3 py-2 rounded-lg text-sm ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-900'
                                } border disabled:opacity-50`}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
                {/* OT Summary */}
                <div className={`p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <Clock className={`w-4 h-4 ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-700'
                        }`}>
                            Overtime
                        </span>
                    </div>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        ₹{totalOTPay.toLocaleString()}
                    </p>
                    <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        {employeesWithOT.length} employees
                    </p>
                </div>

                {/* Incentives Summary */}
                <div className={`p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <Gift className={`w-4 h-4 ${
                            theme === 'dark' ? 'text-green-400' : 'text-green-600'
                        }`} />
                        <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-green-400' : 'text-green-700'
                        }`}>
                            Incentives
                        </span>
                    </div>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        ₹{totalIncentives.toLocaleString()}
                    </p>
                    <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        {incentives.length} entries
                    </p>
                </div>

                {/* Total Additions */}
                <div className={`p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-purple-900/20 border border-purple-800' : 'bg-purple-50 border border-purple-200'
                }`}>
                    <div className="flex items-center gap-2 mb-2">
                        <DollarSign className={`w-4 h-4 ${
                            theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                        }`} />
                        <span className={`text-sm font-medium ${
                            theme === 'dark' ? 'text-purple-400' : 'text-purple-700'
                        }`}>
                            Total Additions
                        </span>
                    </div>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        ₹{(totalOTPay + totalIncentives).toLocaleString()}
                    </p>
                    <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        Added to payroll
                    </p>
                </div>
            </div>

            {/* OT Employees List */}
            {otConfig.enabled && employeesWithOT.length > 0 && (
                <div className={`p-4 border-t ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <h4 className={`font-medium mb-3 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Employees with Overtime ({employeesWithOT.length})
                    </h4>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                                    <th className="text-left py-2 px-3">Employee</th>
                                    <th className="text-center py-2 px-3">Regular OT</th>
                                    <th className="text-center py-2 px-3">Holiday OT</th>
                                    <th className="text-center py-2 px-3">Total Hours</th>
                                    <th className="text-right py-2 px-3">OT Pay</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employeesWithOT.slice(0, 10).map(emp => (
                                    <tr key={emp.id} className={`border-t ${
                                        theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
                                    }`}>
                                        <td className={`py-2 px-3 ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {emp.first_name} {emp.last_name}
                                        </td>
                                        <td className={`text-center py-2 px-3 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            {emp.otCalculation.regularOT} hrs
                                        </td>
                                        <td className={`text-center py-2 px-3 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            {emp.otCalculation.holidayOT} hrs
                                        </td>
                                        <td className={`text-center py-2 px-3 font-medium ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {emp.otCalculation.totalOT} hrs
                                        </td>
                                        <td className={`text-right py-2 px-3 font-medium ${
                                            theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                        }`}>
                                            ₹{emp.otCalculation.totalOTPay.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {employeesWithOT.length > 10 && (
                            <p className={`text-center text-sm mt-2 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                                And {employeesWithOT.length - 10} more employees...
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Incentives Section */}
            <div className={`p-4 border-t ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <div className="flex items-center justify-between mb-3">
                    <h4 className={`font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Incentives & Bonuses
                    </h4>
                    <button
                        onClick={() => setShowAddIncentive(!showAddIncentive)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${
                            showAddIncentive
                                ? theme === 'dark'
                                    ? 'bg-gray-700 text-gray-300'
                                    : 'bg-gray-200 text-gray-700'
                                : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                    >
                        {showAddIncentive ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        {showAddIncentive ? 'Cancel' : 'Add Incentive'}
                    </button>
                </div>

                {/* Add Incentive Form */}
                {showAddIncentive && (
                    <div className={`mb-4 p-4 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'
                    }`}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Employee Select */}
                            <div>
                                <label className={`block text-sm mb-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Employee
                                </label>
                                <select
                                    value={newIncentive.employee_id}
                                    onChange={(e) => setNewIncentive({...newIncentive, employee_id: e.target.value})}
                                    className={`w-full px-3 py-2 rounded-lg text-sm ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    } border`}
                                >
                                    <option value="">Select Employee</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.first_name} {emp.last_name} ({emp.employee_code})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Incentive Type */}
                            <div>
                                <label className={`block text-sm mb-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Type
                                </label>
                                <select
                                    value={newIncentive.type}
                                    onChange={(e) => setNewIncentive({...newIncentive, type: e.target.value})}
                                    className={`w-full px-3 py-2 rounded-lg text-sm ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    } border`}
                                >
                                    {incentiveTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Amount */}
                            <div>
                                <label className={`block text-sm mb-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Amount (₹)
                                </label>
                                <input
                                    type="number"
                                    value={newIncentive.amount}
                                    onChange={(e) => setNewIncentive({...newIncentive, amount: e.target.value})}
                                    placeholder="Enter amount"
                                    className={`w-full px-3 py-2 rounded-lg text-sm ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                                    } border`}
                                />
                            </div>

                            {/* Reason */}
                            <div>
                                <label className={`block text-sm mb-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Reason
                                </label>
                                <input
                                    type="text"
                                    value={newIncentive.reason}
                                    onChange={(e) => setNewIncentive({...newIncentive, reason: e.target.value})}
                                    placeholder="Optional"
                                    className={`w-full px-3 py-2 rounded-lg text-sm ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                                    } border`}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-3">
                            <button
                                onClick={handleAddIncentive}
                                disabled={!newIncentive.employee_id || !newIncentive.amount}
                                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Add Incentive
                            </button>
                        </div>
                    </div>
                )}

                {/* Incentives List */}
                {incentives.length > 0 ? (
                    <div className="space-y-2">
                        {incentives.map(inc => (
                            <div key={inc.id} className={`flex items-center justify-between p-3 rounded-lg ${
                                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                            }`}>
                                <div>
                                    <p className={`font-medium ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        {inc.employee_name}
                                    </p>
                                    <p className={`text-sm ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                    }`}>
                                        {incentiveTypes.find(t => t.value === inc.type)?.label || inc.type}
                                        {inc.reason && ` - ${inc.reason}`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`font-semibold ${
                                        theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                    }`}>
                                        ₹{parseFloat(inc.amount).toLocaleString()}
                                    </span>
                                    <button
                                        onClick={() => handleRemoveIncentive(inc.id)}
                                        className={`p-1.5 rounded hover:bg-red-100 ${
                                            theme === 'dark' ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500'
                                        }`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className={`text-center py-4 ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                        No incentives added yet
                    </p>
                )}
            </div>

            {/* Apply Button */}
            <div className={`p-4 border-t ${
                theme === 'dark' ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
            }`}>
                <div className="flex items-center justify-between">
                    <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Total additions: <span className="font-semibold">₹{(totalOTPay + totalIncentives).toLocaleString()}</span>
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={() => onApply?.({ skip: true })}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${
                                theme === 'dark'
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            Skip OT/Incentives
                        </button>
                        <button
                            onClick={() => onApply?.({
                                otConfig,
                                otCalculations,
                                incentives,
                                totalOTPay,
                                totalIncentives
                            })}
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            Apply to Payroll
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OTIncentivePanel;
